import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import DataTable from '../../components/common/DataTable';
import DateRangePicker from '../../components/common/DateRangePicker';
import { usePagedData } from '../../hooks/usePagedData';
import { settlementReportsApi } from '../../api/endpoints';
import { SettlementReportList } from '../../types';
import Select from '../../components/common/Select';
import { FiRefreshCw, FiSearch, FiDollarSign, FiFileText, FiCheckCircle, FiClock, FiPackage, FiCheck, FiX } from 'react-icons/fi';
import { SkeletonTable } from '../../components/common/Skeleton';
import { toast } from 'react-toastify';
import CountryFlag from '../../components/common/CountryFlag';
import ReparseAllDialog from '../../components/common/ReparseAllDialog';

/* ------------------------------------------------------------------ */
/*  Marketplace name → ISO country code                                */
/* ------------------------------------------------------------------ */

const marketplaceToCountry: Record<string, string> = {
  'amazon.de': 'DE', 'amazon.fr': 'FR', 'amazon.it': 'IT', 'amazon.es': 'ES',
  'amazon.co.uk': 'GB', 'amazon.com': 'US', 'amazon.pl': 'PL', 'amazon.se': 'SE',
  'amazon.nl': 'NL', 'amazon.com.be': 'BE', 'amazon.co.jp': 'JP', 'amazon.ca': 'CA',
  'amazon.com.au': 'AU', 'amazon.com.mx': 'MX', 'amazon.com.tr': 'TR',
  // Marketplace IDs as fallback (some reports return raw IDs)
  'A1PA6795UKMFR9': 'DE', 'A13V1IB3VIYZZH': 'FR', 'APJ6JRA9NG5V4': 'IT',
  'A1RKKUPIHCS9HS': 'ES', 'A1F83G8C2ARO7P': 'GB', 'A1805IZSGTT6HS': 'NL',
  'A1C3SOZRARQ6R3': 'PL', 'A2NODRKZP88ZB9': 'SE',
};

const getMarketplaceCountry = (marketplace: string | null): string | null => {
  if (!marketplace) return null;
  const key = marketplace.toLowerCase().replace(/^https?:\/\//, '').replace(/\/$/, '');
  return marketplaceToCountry[key] || marketplaceToCountry[marketplace] || null;
};

/**
 * Settlement reports often come as Pan-EU — Marketplace field holds a CSV of up to 19
 * marketplace IDs. Parse it into individual country codes (deduped, in input order).
 * Returns: { countries: ISO[], unknown: rawIds[], total: number }.
 */
const parseMarketplaceList = (marketplace: string | null) => {
  if (!marketplace) return { countries: [] as string[], unknown: [] as string[], total: 0 };
  const ids = marketplace.split(',').map(s => s.trim()).filter(Boolean);
  const seen = new Set<string>();
  const countries: string[] = [];
  const unknown: string[] = [];
  for (const id of ids) {
    const cc = getMarketplaceCountry(id);
    if (cc && !seen.has(cc)) { seen.add(cc); countries.push(cc); }
    else if (!cc) unknown.push(id);
  }
  return { countries, unknown, total: ids.length };
};

/* ------------------------------------------------------------------ */
/*  Status badge                                                       */
/* ------------------------------------------------------------------ */

const statusColors: Record<string, { color: string; bg: string }> = {
  New: { color: '#3b82f6', bg: 'rgba(59, 130, 246, 0.12)' },
  Processed: { color: '#10b981', bg: 'rgba(16, 185, 129, 0.12)' },
  ExportedToSap: { color: '#7c3aed', bg: 'rgba(124, 58, 237, 0.12)' },
};

const SettlementStatusBadge: React.FC<{ status: string; t: any }> = ({ status, t }) => {
  const cfg = statusColors[status] || { color: '#6b7280', bg: 'rgba(107, 114, 128, 0.12)' };
  const labelMap: Record<string, string> = {
    New: t('settlements.statusNew'),
    Processed: t('settlements.statusProcessed'),
    ExportedToSap: t('settlements.statusExportedToSap'),
  };
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: '6px',
      padding: '3px 10px', borderRadius: '12px', fontSize: '12px',
      fontWeight: 500, color: cfg.color, backgroundColor: cfg.bg, whiteSpace: 'nowrap',
    }}>
      <span style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: cfg.color, display: 'inline-block', flexShrink: 0 }} />
      {labelMap[status] || status}
    </span>
  );
};

/* ------------------------------------------------------------------ */
/*  Summary stat card                                                  */
/* ------------------------------------------------------------------ */

interface StatCardProps {
  icon: React.ReactNode;
  iconBg: string;
  iconColor: string;
  label: string;
  value: string | number;
}

const StatCard: React.FC<StatCardProps> = ({ icon, iconBg, iconColor, label, value }) => (
  <div style={{
    display: 'flex', alignItems: 'center', gap: '12px',
    padding: '14px 16px', borderRadius: '10px',
    backgroundColor: 'var(--bg-elevated)', border: '1px solid var(--border)',
  }}>
    <div style={{
      width: '36px', height: '36px', borderRadius: '8px', display: 'flex',
      alignItems: 'center', justifyContent: 'center',
      backgroundColor: iconBg, color: iconColor, fontSize: '16px', flexShrink: 0,
    }}>
      {icon}
    </div>
    <div style={{ minWidth: 0 }}>
      <div style={{ fontSize: '11px', color: 'var(--text-muted)', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
        {label}
      </div>
      <div style={{ fontSize: '20px', fontWeight: 700, color: 'var(--text)' }}>
        {value}
      </div>
    </div>
  </div>
);

/* ------------------------------------------------------------------ */
/*  Summary shape                                                      */
/* ------------------------------------------------------------------ */

interface SummaryData {
  totalReports: number;
  totalAmount: number;
  currency: string;
  newCount: number;
  processedCount: number;
}

/* ------------------------------------------------------------------ */
/*  Main page                                                          */
/* ------------------------------------------------------------------ */

const SettlementReportsPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchInput, setSearchInput] = useState('');
  const [syncing, setSyncing] = useState(false);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [summary, setSummary] = useState<SummaryData | null>(null);
  const [reparseAllOpen, setReparseAllOpen] = useState(false);
  const [reparseAllLoading, setReparseAllLoading] = useState(false);

  const {
    data,
    loading,
    query,
    filters,
    setFilters,
    setPage,
    setPageSize,
    setSort,
    setSearch,
    refresh,
  } = usePagedData<SettlementReportList>(settlementReportsApi.list, {
    initialSortBy: 'periodStart',
    initialSortDesc: true,
  });

  useEffect(() => {
    let cancelled = false;
    const fetchSummary = async () => {
      try {
        const resp = await settlementReportsApi.summary(filters);
        if (!cancelled) setSummary(resp.data);
      } catch {
        // Silently fail - stats are non-critical
      }
    };
    fetchSummary();
    return () => { cancelled = true; };
  }, [filters]);

  const handleSearchSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      setSearch(searchInput);
    },
    [searchInput, setSearch]
  );

  const handleStatusFilter = useCallback(
    (value: string) => {
      setFilters((prev: Record<string, any>) => ({
        ...prev,
        status: value === '' ? undefined : value,
      }));
    },
    [setFilters]
  );

  const handleMarketplaceFilter = useCallback(
    (value: string) => {
      setFilters((prev: Record<string, any>) => ({
        ...prev,
        marketplace: value === '' ? undefined : value,
      }));
    },
    [setFilters]
  );

  const handleDateFromChange = useCallback(
    (value: string) => {
      setDateFrom(value);
      setFilters((prev: Record<string, any>) => ({
        ...prev,
        dateFrom: value || undefined,
      }));
    },
    [setFilters]
  );

  const handleDateToChange = useCallback(
    (value: string) => {
      setDateTo(value);
      setFilters((prev: Record<string, any>) => ({
        ...prev,
        dateTo: value || undefined,
      }));
    },
    [setFilters]
  );

  const handleSync = useCallback(async () => {
    setSyncing(true);
    try {
      await settlementReportsApi.sync();
      toast.success(t('settlements.syncStarted'));
      refresh();
    } catch (error: any) {
      toast.error(error.response?.data?.message || t('settlements.syncError'));
    } finally {
      setSyncing(false);
    }
  }, [refresh, t]);

  const handleReparseAll = useCallback(async () => {
    setReparseAllLoading(true);
    try {
      const response = await settlementReportsApi.reparseAll();
      const reports = response.data?.reportsTouched ?? 0;
      const transactions = response.data?.transactionsTotal ?? 0;
      toast.success(t('settlements.reparseAllSuccess', { reports, transactions }));
      // Don't auto-close the dialog on success — the dialog itself shows a
      // completion banner driven by the SignalR "Completed" event, and the
      // user clicks "Close" when they've absorbed the result. Closing here
      // would race with the SignalR event and the user would see the
      // completion banner flash for ~50ms before the modal vanishes.

      // Refresh the list and summary so the new (non-zero) totals show up.
      refresh();
      try {
        const resp = await settlementReportsApi.summary(filters);
        setSummary(resp.data);
      } catch {
        // Non-critical — list refresh already covers the user's main view.
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || t('settlements.reparseAllError'));
      setReparseAllOpen(false);  // close on error so user can retry from scratch
    } finally {
      setReparseAllLoading(false);
    }
  }, [refresh, t, filters]);

  const handleRowClick = useCallback(
    (row: SettlementReportList) => {
      navigate(`/settlements/${row.id}`);
    },
    [navigate]
  );

  // Counts of CSV-eligible vs not-eligible reports for the modal preview chips.
  // Only reflects the current page of data (because that's all we have client-side),
  // but the user gets a useful "X of Y on this page have CSV" preview either way.
  const csvStats = useMemo(() => {
    if (!data?.items) return { total: 0, withCsv: 0 };
    let withCsv = 0;
    for (const r of data.items) if (r.hasRawCsv) withCsv++;
    return { total: data.items.length, withCsv };
  }, [data?.items]);

  const formatCurrency = (amount: number, currency?: string) => {
    const cur = currency || 'EUR';
    return amount.toLocaleString('pl-PL', { style: 'currency', currency: cur });
  };

  const formatDate = (dateStr: string) =>
    new Date(dateStr).toLocaleDateString('pl-PL', { day: '2-digit', month: '2-digit', year: 'numeric' });

  const columns = useMemo(() => [
    {
      key: 'period',
      header: t('settlements.period'),
      sortable: true,
      render: (row: SettlementReportList) => (
        <span style={{ fontFamily: "'JetBrains Mono', 'Fira Code', 'Consolas', monospace", fontSize: '13px', fontWeight: 600 }}>
          {formatDate(row.periodStart)} &ndash; {formatDate(row.periodEnd)}
        </span>
      ),
    },
    {
      key: 'marketplace',
      header: t('settlements.marketplace'),
      className: 'col-hide-mobile',
      render: (row: SettlementReportList) => {
        const { countries, unknown, total } = parseMarketplaceList(row.marketplace);
        // Single-marketplace report: render as before (flag + text).
        if (total <= 1) {
          return (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
              <CountryFlag countryCode={getMarketplaceCountry(row.marketplace)} size={14} />
              <span style={{ fontSize: '12px', color: 'var(--text-muted)' }}>{row.marketplace || '-'}</span>
            </span>
          );
        }
        // Pan-EU / multi-marketplace: stack flags, show count.
        const flagsToShow = countries.slice(0, 6);
        const remaining = countries.length - flagsToShow.length + unknown.length;
        const tooltip = [...countries, ...unknown.map(id => `[${id}]`)].join(', ');
        return (
          <span
            style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', flexWrap: 'wrap' }}
            title={tooltip}
          >
            {flagsToShow.map(cc => (
              <CountryFlag key={cc} countryCode={cc} size={14} />
            ))}
            {remaining > 0 && (
              <span style={{
                fontSize: '11px', color: 'var(--text-muted)',
                background: 'var(--bg-secondary, rgba(255,255,255,0.06))',
                padding: '1px 6px', borderRadius: '8px', fontWeight: 500,
              }}>
                +{remaining}
              </span>
            )}
            <span style={{ fontSize: '11px', color: 'var(--text-muted)', marginLeft: '4px' }}>
              ({total} {t('settlements.marketplaces') || 'rynków'})
            </span>
          </span>
        );
      },
    },
    {
      key: 'status',
      header: t('common.status'),
      sortable: true,
      render: (row: SettlementReportList) => <SettlementStatusBadge status={row.status} t={t} />,
    },
    {
      key: 'transactionCount',
      header: t('settlements.transactions'),
      render: (row: SettlementReportList) => (
        <span style={{
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          minWidth: '26px', height: '22px', padding: '0 8px',
          borderRadius: '11px', fontSize: '12px', fontWeight: 600,
          backgroundColor: 'rgba(107, 114, 128, 0.1)', color: 'var(--text)',
        }}>
          {row.transactionCount}
        </span>
      ),
    },
    {
      // CSV availability — directly tells the user whether the bulk-reparse repair
      // can fix this row, and whether they can still download the original Amazon CSV.
      key: 'hasRawCsv',
      header: t('settlements.csvColumn'),
      render: (row: SettlementReportList) => row.hasRawCsv ? (
        <span
          title={t('settlements.csvAvailable')}
          style={{
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            width: '24px', height: '24px', borderRadius: '50%',
            backgroundColor: 'rgba(16, 185, 129, 0.15)', color: '#10b981',
          }}
        >
          <FiCheck size={14} strokeWidth={3} />
        </span>
      ) : (
        <span
          title={t('settlements.csvMissing')}
          style={{
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            width: '24px', height: '24px', borderRadius: '50%',
            backgroundColor: 'rgba(239, 68, 68, 0.12)', color: '#ef4444',
          }}
        >
          <FiX size={14} strokeWidth={3} />
        </span>
      ),
    },
    {
      key: 'totalAmount',
      header: t('settlements.amount'),
      sortable: true,
      render: (row: SettlementReportList) => (
        <span style={{ fontWeight: 600, color: row.totalAmount >= 0 ? '#10b981' : '#ef4444' }}>
          {formatCurrency(row.totalAmount, row.currency)}
        </span>
      ),
    },
  ], [t]);

  return (
    <div className="page-container page-container--wide">
      <div className="page-header">
        <h1>{t('settlements.title')}</h1>
        <div className="page-actions" style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
          <button
            type="button"
            className="btn"
            onClick={() => setReparseAllOpen(true)}
            disabled={reparseAllLoading}
            title={t('settlements.reparseAllModalIntro')}
            style={{
              backgroundColor: '#f97316',
              borderColor: '#f97316',
              color: '#fff',
              fontWeight: 600,
            }}
          >
            <FiRefreshCw className={reparseAllLoading ? 'spin' : ''} />
            {reparseAllLoading ? t('settlements.reparseAllRunning') : t('settlements.reparseAll')}
          </button>
          <button
            className="btn btn-primary"
            onClick={handleSync}
            disabled={syncing}
          >
            <FiRefreshCw className={syncing ? 'spin' : ''} />
            {syncing ? t('settlements.syncing') : t('settlements.syncWithAmazon')}
          </button>
        </div>
      </div>

      <ReparseAllDialog
        isOpen={reparseAllOpen}
        loading={reparseAllLoading}
        totalReports={csvStats.total}
        reportsWithCsv={csvStats.withCsv}
        onCancel={() => { if (!reparseAllLoading) setReparseAllOpen(false); }}
        onConfirm={handleReparseAll}
      />

      {summary && (
        <div className="po-stats-grid">
          <StatCard
            icon={<FiFileText />}
            iconBg="rgba(59, 130, 246, 0.15)"
            iconColor="#3b82f6"
            label={t('settlements.statTotalReports')}
            value={summary.totalReports}
          />
          <StatCard
            icon={<FiDollarSign />}
            iconBg="rgba(16, 185, 129, 0.15)"
            iconColor="#10b981"
            label={t('settlements.statTotalAmount')}
            value={formatCurrency(summary.totalAmount, summary.currency)}
          />
          <StatCard
            icon={<FiClock />}
            iconBg="rgba(59, 130, 246, 0.15)"
            iconColor="#3b82f6"
            label={t('settlements.statusNew')}
            value={summary.newCount}
          />
          <StatCard
            icon={<FiCheckCircle />}
            iconBg="rgba(16, 185, 129, 0.15)"
            iconColor="#10b981"
            label={t('settlements.statusProcessed')}
            value={summary.processedCount}
          />
        </div>
      )}

      <div className="toolbar">
        <form className="toolbar-search" onSubmit={handleSearchSubmit}>
          <div className="input-group">
            <input
              type="text"
              className="form-control"
              placeholder={t('settlements.searchPlaceholder')}
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
            />
            <button type="submit" className="btn btn-secondary">
              <FiSearch />
            </button>
          </div>
        </form>

        <div className="toolbar-filters">
          <Select
            options={[
              { value: '', label: t('common.allStatuses') },
              { value: 'New', label: t('settlements.statusNew') },
              { value: 'Processed', label: t('settlements.statusProcessed') },
              { value: 'ExportedToSap', label: t('settlements.statusExportedToSap') },
            ]}
            value={String(filters.status ?? '')}
            onChange={handleStatusFilter}
            placeholder={t('common.allStatuses')}
          />

          <Select
            options={[
              { value: '', label: t('settlements.allMarketplaces') },
              { value: 'amazon.de', label: 'Amazon.de' },
              { value: 'amazon.fr', label: 'Amazon.fr' },
              { value: 'amazon.it', label: 'Amazon.it' },
              { value: 'amazon.es', label: 'Amazon.es' },
              { value: 'amazon.co.uk', label: 'Amazon.co.uk' },
              { value: 'amazon.pl', label: 'Amazon.pl' },
              { value: 'amazon.nl', label: 'Amazon.nl' },
              { value: 'amazon.se', label: 'Amazon.se' },
            ]}
            value={String(filters.marketplace ?? '')}
            onChange={handleMarketplaceFilter}
            placeholder={t('settlements.allMarketplaces')}
          />
        </div>
      </div>

      <div style={{ marginBottom: '12px' }}>
        <DateRangePicker
          dateFrom={dateFrom}
          dateTo={dateTo}
          onDateFromChange={handleDateFromChange}
          onDateToChange={handleDateToChange}
        />
      </div>

      {loading ? (
        <SkeletonTable rows={8} cols={5} />
      ) : (
        <DataTable
          columns={columns}
          data={data?.items || []}
          totalCount={data?.totalCount || 0}
          page={query.page}
          pageSize={query.pageSize}
          totalPages={data?.totalPages || 0}
          sortBy={query.sortBy}
          sortDescending={query.sortDescending}
          onPageChange={setPage}
          onPageSizeChange={setPageSize}
          onSort={setSort}
          onRowClick={handleRowClick}
        />
      )}
    </div>
  );
};

export default SettlementReportsPage;
